import React, {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {SequenceStateStore} from "../store/SequenceStateStore";
import {SequenceTimelineStore} from "../store/SequenceTimelineStore";
import { TIMELINE_RULER_TICK_WIDTH_PX } from "../../librettoEditor/config/config";
import { SequenceCompositionService } from "../services/SequenceCompositionService";
import {autorun} from "mobx";
import './SequenceTimeline.styles.scss';
import {TrackLane} from "./TrackLane";
import {TIMELINE_CONTROLS_ELEMENT_ID, TIMELINE_ELEMENT_ID, TIMELINE_TRACKS_CONTAINER_ID} from "../config/constants";
import SequenceCanvasTimeRuler from "./SequenceCanvasTimeRuler";
import SequenceControls from "./SequenceControls";
import SequenceTrackDivider from "./SequenceTrackDivider";
import SequencePlayhead from "./SequencePlayhead";
import SequenceTrackNameField, {
    SEQUENCE_TRACK_LABELS_WIDTH_PX,
    SEQUENCE_TRACK_ROW_HEIGHT,
} from "./SequenceTrackNameField";

const getService = (): SequenceCompositionService | null => {
  // Service is owned by canvas section; the timeline does not need a global ref.
  // If needed later, we can pass a getter via props.
  // For now, return null so play/pause buttons are hidden; we will inject later.
  return null;
};

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 5.0;

const ZOOM_SENSITIVITY = 0.0025; // tweak this

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export const SequenceTimeline: React.FC = observer(() => {
    const [playheadX, setPlayheadX] = useState(0);
    const [tracksScrollTop, setTracksScrollTop] = useState(0);
    const [orderedLayerIds, setOrderedLayerIds] = useState<string[]>(() => SequenceStateStore.orderedLayerIds ?? []);
    // const [librettoClipLayers, setLibrettoClipLayers] = useState(CompositionStore.layerToLibrettoClips);
    const [zoom, setZoom] = useState(SequenceTimelineStore.zoom);
    const [duration, setDuration] = useState(SequenceStateStore.duration);

    const timelineTracksRef = useRef(null);
    const timelineTracksResizeObserver = useRef(null);

    useEffect(() => {
        const disposeAutorun = autorun(() => {
            // This will re-run whenever any observable accessed inside changes
            // setLibrettoClipLayers(CompositionStore.layerToLibrettoClips);
            setOrderedLayerIds(SequenceStateStore.orderedLayerIds);
            setDuration(SequenceStateStore.duration);
            setZoom(SequenceTimelineStore.zoom);
        });

        return () => {
            disposeAutorun();
            removeScrollSync();
            // @ts-ignore
            timelineTracksResizeObserver.current?.disconnect();
        };
    }, []);

    useEffect(() => {
        // initialize playhead position relative to left pad on mount
        handleScroll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        SequenceTimelineStore.setWidth(SequenceStateStore.duration * TIMELINE_RULER_TICK_WIDTH_PX);
    }, [duration]);

    useEffect(() => {
        addScrollSync();

        // @ts-ignore
        timelineTracksResizeObserver.current = new ResizeObserver(handleTimelineTracksResize);
        if (timelineTracksRef.current) {
            // @ts-ignore
            timelineTracksResizeObserver.current.observe(timelineTracksRef.current);
        }

        return () => {
            removeScrollSync();
            // @ts-ignore
            timelineTracksResizeObserver.current?.disconnect();
        };
    }, []);

    const handleScroll = () => {
        if (!timelineTracksRef.current) return;

        // @ts-ignore
        const sl = timelineTracksRef.current.scrollLeft;
        // @ts-ignore
        const st = timelineTracksRef.current.scrollTop ?? 0;
        const leftPadPx = (SequenceTimelineStore.leftPadSeconds ?? 3) * TIMELINE_RULER_TICK_WIDTH_PX * SequenceTimelineStore.zoom;
        setPlayheadX(SEQUENCE_TRACK_LABELS_WIDTH_PX + leftPadPx - sl);
        setTracksScrollTop(st);

        const event = new CustomEvent('sequenceTracksScrollLeft', {
            bubbles: true,
            // @ts-ignore
            detail: timelineTracksRef.current.scrollLeft
        });
        // @ts-ignore
        timelineTracksRef.current.dispatchEvent(event);
    };

    const addScrollSync = () => {
        // @ts-ignore
        timelineTracksRef.current?.addEventListener("scroll", handleScroll);
    };

    const removeScrollSync = () => {
        // @ts-ignore
        timelineTracksRef.current?.removeEventListener("scroll", handleScroll);
    };

    // @ts-ignore
    const handleTimelineTracksResize = (entries) => {
        const width = entries[0]?.contentRect?.width;
        if (width) {
            SequenceTimelineStore.setTracksContainerWidth(width);
        }
    };

    // @ts-ignore
    // const handleCreateLayerMedia = async (event, mediaId, index) => {
    //     const startTime = convertUnitsToTime(event.nativeEvent.offsetX);
    //     const layer = CompositionService.createLayer(index);
    //     const clip = await CompositionService.addMediaToLayer(layer.id, mediaId, startTime);
    //     if (clip) {
    //         // let librettoClipId = clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) as string;
    //         // if (librettoClipId === null || librettoClipId === "" || librettoClipId === undefined) {
    //         //     librettoClipId = GenerateLibrettoClipId();
    //         //     clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
    //         // }
    //         //ApplicationStore.setSelectedLibrettoClipId(librettoClipId);
    //     }
    // };

    // @ts-ignore
    // const handleCreateLayerText = async (event, title, index) => {
    //     const layer = CompositionService.createLayer(index);
    //     const startTime = convertUnitsToTime(event.nativeEvent.offsetX);
    //     const clip = await CompositionService.createLottieClip({
    //         dataUrl: title.dataUrl,
    //         propertiesUrl: title.propertiesUrl,
    //         startTime: startTime,
    //         layerId: layer.id,
    //     });
    //     if (clip) {
    //         // let librettoClipId = clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) as string;
    //         // if (librettoClipId === null || librettoClipId === "" || librettoClipId === undefined) {
    //         //     librettoClipId = GenerateLibrettoClipId();
    //         //     clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
    //         // }
    //         //ApplicationStore.setSelectedLibrettoClipId(librettoClipId);
    //     }
    // };

    const handleWheel = (event) => {
        const container = timelineTracksRef.current;
        if (!container) return;

        event.stopPropagation();

        if (event.ctrlKey) {
            // Interpret as zoom
            const zoomChange = -event.deltaY * ZOOM_SENSITIVITY; // Negative to match natural zoom direction
            const newZoom = clamp(SequenceTimelineStore.zoom + zoomChange, MIN_ZOOM, MAX_ZOOM);
            SequenceTimelineStore.setZoom(newZoom);
        } else {
            container.scrollLeft += event.deltaY + event.deltaX;
        }
    };

    // @ts-ignore
    const renderTrackDivider = (index) => {
        const newIndex = layersOrder.length - index;
        return (
            <SequenceTrackDivider
                index={newIndex}
                // @ts-ignore
                onDropMediaClip={(event, mediaId) => {}}
                // @ts-ignore
                onDropTextClip={(event, title) => {}}
            />
        );
    };

    useEffect(() => {
        // @ts-ignore
        const handleScrollToPage = (event) => {
            console.log("Handle scroll to page event: ", event.detail);
            const trackOffsetWidth = SequenceTimelineStore.tracksContainerWidth;
            const page = event.detail;
            if (timelineTracksRef.current) {
                // @ts-ignore
                console.log("Scroll to page: ", page, " Track offset width: ", trackOffsetWidth);
                timelineTracksRef.current.scrollLeft = page * trackOffsetWidth;
            }
        };

        window.addEventListener('sequenceScrollToPage', handleScrollToPage);
        return () => window.removeEventListener('sequenceScrollToPage', handleScrollToPage);
    }, []);

    const layersOrder = [...orderedLayerIds].reverse();
    const LEFT_PAD_SECONDS = SequenceTimelineStore.leftPadSeconds ?? 3;
    const leftPadPx = LEFT_PAD_SECONDS * TIMELINE_RULER_TICK_WIDTH_PX * SequenceTimelineStore.zoom;
    const tracksWidth = (SequenceStateStore.duration * TIMELINE_RULER_TICK_WIDTH_PX * SequenceTimelineStore.zoom) + leftPadPx;

    return (
        <div id={TIMELINE_ELEMENT_ID} className="timeline">
            <div id={TIMELINE_CONTROLS_ELEMENT_ID} className="timeline__controls">
                <SequenceControls/>
            </div>

            <div className="timeline__container" onWheel={handleWheel}>
                {/*{layersOrder.length === 0 && <EmptyTimelineButton/>}*/}

                <div className="timeline__canvas-ruler">
                    <div className="timeline__ruler-row">
                        <div className="timeline__ruler-spacer" />
                        <div className="timeline__ruler-track-area">
                            <SequenceCanvasTimeRuler />
                        </div>
                    </div>
                </div>

                <div className="timeline__playhead" style={{ transform: `translateX(calc(-50% + ${playheadX}px))` }}>
                    {/* @ts-ignore */}
                    <SequencePlayhead containerElement={document} />
                </div>

                <div className="timeline__divider-top">{renderTrackDivider(0)}</div>

                <div className="timeline__tracks-wrapper">
                    <div className="timeline__track-labels">
                        <div
                            className="timeline__track-labels-inner"
                            style={{ transform: `translateY(-${tracksScrollTop}px)` }}
                        >
                            {layersOrder.map((layerId) => (
                                <SequenceTrackNameField key={layerId} layerId={layerId} />
                            ))}
                            {layersOrder.length === 0 && <div style={{ height: SEQUENCE_TRACK_ROW_HEIGHT }} />}
                        </div>
                    </div>

                    <div
                        id={TIMELINE_TRACKS_CONTAINER_ID}
                        className="timeline__tracks"
                        onMouseDown={() => {
                            //ApplicationStore.setSelectedLibrettoClipIdToNull();
                        }}
                        ref={timelineTracksRef}
                    >
                        {layersOrder.length > 0 && (
                            <div className="tracks" style={{ minWidth: `${tracksWidth}px`, display: "flex" }}>
                                <div style={{ minWidth: `${leftPadPx}px` }} />
                                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                                    {layersOrder.map((layerId, index) => (
                                        // @ts-ignore
                                        <TrackLane key={layerId} layerId={layerId}>
                                            {index < layersOrder.length - 1 && renderTrackDivider(index + 1)}
                                        </TrackLane>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="timeline__void-container">{renderTrackDivider(layersOrder.length)}</div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default SequenceTimeline;
